/**
 * Validates that a date range is valid (startDate < endDate).
 * Returns true if the range is valid, false otherwise.
 *
 * Both dates are compared in UTC to ensure timezone consistency.
 *
 * IMPORTANT: Both startDate and endDate must be provided. Callers must ensure
 * both dates are present before calling this function.
 *
 * @param {Date | string | null | undefined} startDate - The start date (required)
 * @param {Date | string | null | undefined} endDate - The end date (required)
 * @returns {boolean} true if startDate < endDate, false if startDate >= endDate or if dates are invalid
 * @throws {Error} if either date is null or undefined
 *
 * @example
 * // Validate a valid date range
 * const isValid = isValidDateRange(new Date('2024-01-15T09:00:00Z'), new Date('2024-01-15T10:00:00Z'));
 * console.log(isValid); // Outputs: true
 *
 * @example
 * // Invalid range (startDate >= endDate)
 * const isValid = isValidDateRange(new Date('2024-01-15T10:00:00Z'), new Date('2024-01-15T09:00:00Z'));
 * console.log(isValid); // Outputs: false
 */
export function isValidDateRange(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined
): boolean {
  // Require both dates to be present
  if (startDate == null || endDate == null) {
    throw new Error(
      'isValidDateRange requires both startDate and endDate to be provided'
    );
  }

  // Convert to Date objects if needed
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  // Check if dates are valid
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }

  // Compare using UTC timestamps to ensure timezone consistency
  // Both dates are converted to UTC milliseconds for accurate comparison
  const startTimestamp = start.getTime();
  const endTimestamp = end.getTime();

  // Validate that startDate < endDate
  return startTimestamp < endTimestamp;
}
